/**
 * CloudWatch-Style Logs Display Component
 *
 * A comprehensive logs viewer modeled after AWS CloudWatch Logs with advanced filtering,
 * log expansion, export functionality, and real-time search capabilities.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  CopyButton,
  Group,
  Menu,
  MultiSelect,
  Paper,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconCopy,
  IconDownload,
  IconFilter,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import type { LogEntry } from "../../types";

interface LogsDisplayProps {
  logs: LogEntry[];
  logSourceType: "cloudwatch" | "database" | null;
  onRefresh?: () => void;
}

// Border colors for each log level
const LOG_LEVEL_BORDER_COLORS: Record<string, string | undefined> = {
  INFO: undefined, // No border - matches background
  WARNING: "#fab005", // Yellow/Orange
  ERROR: "#fa5252", // Red
  DEBUG: undefined, // No border - matches background
};

// Background colors for each log level (only WARNING and ERROR)
const LOG_LEVEL_BACKGROUNDS: Record<string, string | undefined> = {
  INFO: undefined, // No background
  WARNING: "#fff4e6", // Light orange
  ERROR: "#ffe9e9", // Light red
  DEBUG: undefined, // No background
};

// Color mapping for log origins/sources (avoiding orange and red)
const LOG_ORIGIN_COLORS: Record<string, string> = {
  backend: "#228be6", // blue
  frontend: "#12b886", // teal
  sidepanel: "#7950f2", // violet
  tabcontent: "#40c057", // green
  // Default fallback color
  default: "#495057", // dark
};

// Helper function to get origin color
const getOriginColor = (origin: string): string => {
  return LOG_ORIGIN_COLORS[origin.toLowerCase()] || LOG_ORIGIN_COLORS.default;
};

// Extract time portion from timestamp, omitting the date
const formatTimeOnly = (timestamp: string): string => {
  if (timestamp.includes("T")) return timestamp.split("T")[1];
  if (timestamp.includes(" ")) return timestamp.split(" ").slice(1).join(" ");
  return timestamp;
};

const LOG_LEVEL_OPTIONS = [
  { value: "INFO", label: "INFO" },
  { value: "WARNING", label: "WARNING" },
  { value: "ERROR", label: "ERROR" },
  { value: "DEBUG", label: "DEBUG" },
];

// Normalize log level to handle lowercase and truncated values
const normalizeLogLevel = (
  level: string
): "INFO" | "WARNING" | "ERROR" | "DEBUG" => {
  const upperLevel = level.toUpperCase();

  // Handle truncated log levels
  const levelMapping: Record<string, "INFO" | "WARNING" | "ERROR" | "DEBUG"> = {
    WARN: "WARNING",
    ERR: "ERROR",
    INF: "INFO",
    DBG: "DEBUG",
  };

  const normalized = levelMapping[upperLevel] || upperLevel;

  // Validate it's a known level
  if (["INFO", "WARNING", "ERROR", "DEBUG"].includes(normalized)) {
    return normalized as "INFO" | "WARNING" | "ERROR" | "DEBUG";
  }

  return "INFO"; // Default fallback
};

/**
 * Component that displays log message with automatic truncation to 3 lines
 * and shows expand/collapse only when needed
 */
interface LogMessageProps {
  message: string;
  isExpanded: boolean;
  onToggle: () => void;
}

const LogMessage: React.FC<LogMessageProps> = ({
  message,
  isExpanded,
  onToggle,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [needsTruncation, setNeedsTruncation] = useState(false);

  useEffect(() => {
    // Check if text exceeds 3 lines
    if (textRef.current) {
      // Temporarily remove line clamp to get true height
      const element = textRef.current;
      const computedStyle = getComputedStyle(element);
      const lineHeight = parseFloat(computedStyle.lineHeight);

      // Get the actual scrollHeight when not clamped
      const previousDisplay = element.style.display;
      const previousWebkitLineClamp = element.style.webkitLineClamp;
      const previousWebkitBoxOrient = element.style.webkitBoxOrient;

      element.style.display = "block";
      element.style.webkitLineClamp = "unset";
      element.style.webkitBoxOrient = "unset";

      const actualHeight = element.scrollHeight;
      const maxHeight = lineHeight * 3;

      // Restore original styles
      element.style.display = previousDisplay;
      element.style.webkitLineClamp = previousWebkitLineClamp;
      element.style.webkitBoxOrient = previousWebkitBoxOrient;

      // Only show button if content truly exceeds 3 lines (with small tolerance)
      setNeedsTruncation(actualHeight > maxHeight + 2);
    }
  }, [message]);

  return (
    <Box style={{ flex: 1 }}>
      <Text
        ref={textRef}
        size="sm"
        ff="monospace"
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          display: "-webkit-box",
          WebkitLineClamp: isExpanded ? "unset" : 3,
          WebkitBoxOrient: "vertical",
          overflow: isExpanded ? "visible" : "hidden",
          transition: "all 0.2s ease-in-out",
        }}
      >
        {message}
      </Text>
      {needsTruncation && (
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 8,
          }}
        >
          <Button
            variant="subtle"
            size="xs"
            onClick={onToggle}
            style={{
              fontWeight: 500,
            }}
          >
            {isExpanded ? "Show less" : "Show more"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export const LogsDisplay: React.FC<LogsDisplayProps> = ({
  logs,
  logSourceType,
  onRefresh,
}) => {
  // Normalize all log levels on mount
  const normalizedLogs = useMemo(() => {
    return logs.map((log) => ({
      ...log,
      level: normalizeLogLevel(log.level),
    }));
  }, [logs]);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [levelFilters, setLevelFilters] = useState<string[]>([]);
  const [showTimestamp, setShowTimestamp] = useState(true);
  const [showSource, setShowSource] = useState(true);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());

  // Extract unique log origins for filtering
  const logOrigins = useMemo(() => {
    const origins = new Set(
      normalizedLogs.map((log) => log.origin).filter(Boolean)
    );
    return Array.from(origins).sort();
  }, [normalizedLogs]);

  const [originFilters, setOriginFilters] = useState<string[]>([]);

  // Filter logs based on all criteria
  const filteredLogs = useMemo(() => {
    return normalizedLogs.filter((log) => {
      // Level filter
      const matchesLevel =
        levelFilters.length === 0 || levelFilters.includes(log.level);

      // Origin filter
      const matchesOrigin =
        originFilters.length === 0 || originFilters.includes(log.origin);

      // Search query (searches message, timestamp, origin, and module)
      const matchesSearch =
        !searchQuery ||
        log.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.timestamp.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.origin.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (log.module || "").toLowerCase().includes(searchQuery.toLowerCase());

      return matchesLevel && matchesOrigin && matchesSearch;
    });
  }, [normalizedLogs, levelFilters, originFilters, searchQuery]);

  // Toggle log expansion
  const toggleLogExpansion = (index: number) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedLogs(newExpanded);
  };

  // Export logs functionality
  const exportLogs = (format: "json" | "txt") => {
    let content: string;
    let filename: string;
    let mimeType: string;

    if (format === "json") {
      content = JSON.stringify(filteredLogs, null, 2);
      filename = `logs-${new Date().toISOString()}.json`;
      mimeType = "application/json";
    } else {
      content = filteredLogs
        .map(
          (log) =>
            `[${log.timestamp}] [${log.level}] [${log.origin}]${log.module ? ` [${log.module}]` : ""} ${log.message}`
        )
        .join("\n");
      filename = `logs-${new Date().toISOString()}.txt`;
      mimeType = "text/plain";
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Copy all filtered logs to clipboard
  const copyAllLogs = () => {
    const content = filteredLogs
      .map(
        (log) =>
          `[${log.timestamp}] [${log.level}] [${log.origin}]${log.module ? ` [${log.module}]` : ""} ${log.message}`
      )
      .join("\n");
    navigator.clipboard.writeText(content);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setLevelFilters([]);
    setOriginFilters([]);
  };

  const hasActiveFilters =
    searchQuery !== "" || levelFilters.length > 0 || originFilters.length > 0;

  return (
    <Box>
      <Stack gap="md">
        {/* Header Section */}
        <Group justify="space-between" wrap="nowrap" px="md" pt="md">
          <Group gap="xs" wrap="nowrap">
            <Text size="lg" fw={600}>
              Logs
            </Text>
            {logSourceType && (
              <Badge
                variant="light"
                color={logSourceType === "cloudwatch" ? "blue" : "gray"}
                size="sm"
              >
                {logSourceType}
              </Badge>
            )}
            <Text size="sm" c="dimmed">
              {filteredLogs.length} of {normalizedLogs.length}
            </Text>
          </Group>

          <Group gap="xs" wrap="nowrap">
            {onRefresh && (
              <Tooltip label="Refresh logs">
                <ActionIcon variant="default" onClick={onRefresh} size="md">
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            )}

            <Menu shadow="md" width={200}>
              <Menu.Target>
                <Tooltip label="Export logs">
                  <ActionIcon variant="default" size="md">
                    <IconDownload size={16} />
                  </ActionIcon>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Export Format</Menu.Label>
                <Menu.Item onClick={() => exportLogs("txt")}>
                  Text File (.txt)
                </Menu.Item>
                <Menu.Item onClick={() => exportLogs("json")}>
                  JSON File (.json)
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  onClick={copyAllLogs}
                  leftSection={<IconCopy size={14} />}
                >
                  Copy to Clipboard
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>

            <Menu shadow="md" width={220}>
              <Menu.Target>
                <Tooltip label="Display options">
                  <ActionIcon variant="default" size="md">
                    <IconFilter size={16} />
                  </ActionIcon>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Display Options</Menu.Label>
                <Menu.Item
                  onClick={() => setShowTimestamp(!showTimestamp)}
                  rightSection={showTimestamp ? <IconCheck size={14} /> : null}
                >
                  Show Timestamp
                </Menu.Item>
                <Menu.Item
                  onClick={() => setShowSource(!showSource)}
                  rightSection={showSource ? <IconCheck size={14} /> : null}
                >
                  Show Source
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>

        {/* Filter Controls */}
        <Group align="flex-start" wrap="wrap" px="md">
          <TextInput
            placeholder="Search logs..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ minWidth: 300, flexGrow: 1 }}
          />

          <MultiSelect
            placeholder="Filter by level"
            data={LOG_LEVEL_OPTIONS}
            value={levelFilters}
            onChange={setLevelFilters}
            clearable
            searchable={false}
            style={{ minWidth: 200 }}
          />

          {logOrigins.length > 0 && (
            <MultiSelect
              placeholder="Filter by origin"
              data={logOrigins}
              value={originFilters}
              onChange={setOriginFilters}
              clearable
              searchable
              style={{ minWidth: 200 }}
            />
          )}

          {hasActiveFilters && (
            <Button
              variant="light"
              size="sm"
              onClick={clearFilters}
              color="gray"
            >
              Clear Filters
            </Button>
          )}
        </Group>

        {/* Color Legend */}
        <Group gap="lg" px="md">
          <Text size="xs" fw={600} c="dimmed">
            LOG SOURCES:
          </Text>
          {Object.entries(LOG_ORIGIN_COLORS).map(([origin, color]) => {
            if (origin === "default") return null;
            return (
              <Group gap="xs" key={origin}>
                <Box
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <Text size="xs" c="dimmed" tt="capitalize">
                  {origin}
                </Text>
              </Group>
            );
          })}
        </Group>

        {/* Logs Display */}
        {filteredLogs.length === 0 ? (
          <Paper p="xl" withBorder radius="md">
            <Text c="dimmed" fs="italic" ta="center">
              {normalizedLogs.length === 0
                ? "No logs available"
                : "No logs found matching the current filters"}
            </Text>
          </Paper>
        ) : (
          <Stack gap={0}>
            {filteredLogs.map((log, index) => {
              const isExpanded = expandedLogs.has(index);
              const borderColor = LOG_LEVEL_BORDER_COLORS[log.level];

              return (
                <Group
                  key={index}
                  wrap="nowrap"
                  align="flex-start"
                  gap={0}
                  style={{
                    borderBottom:
                      index < filteredLogs.length - 1
                        ? "1px solid #e9ecef"
                        : undefined,
                  }}
                >
                  {/* Log Source Dot */}
                  {showSource && log.origin && (
                    <Tooltip label={log.origin} position="right">
                      <Box
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: getOriginColor(log.origin),
                          flexShrink: 0,
                          marginTop: 12,
                          marginLeft: 16,
                          marginRight: 12,
                          cursor: "help",
                        }}
                      />
                    </Tooltip>
                  )}

                  {/* Timestamp - Outside colored background */}
                  {showTimestamp && (
                    <Text
                      size="xs"
                      c="dimmed"
                      ff="monospace"
                      style={{
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        marginTop: 10,
                        marginRight: 12,
                      }}
                    >
                      {formatTimeOnly(log.timestamp)}
                    </Text>
                  )}

                  {/* Module name */}
                  {log.module && (
                    <Text
                      size="xs"
                      c="dimmed"
                      ff="monospace"
                      style={{
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        marginTop: 10,
                        marginRight: 12,
                      }}
                    >
                      {log.module}
                    </Text>
                  )}

                  {/* Log Content with colored background */}
                  <Paper
                    radius={0}
                    withBorder={false}
                    shadow="none"
                    style={{
                      ...(borderColor && {
                        borderLeft: `4px solid ${borderColor}`,
                      }),
                      backgroundColor: LOG_LEVEL_BACKGROUNDS[log.level],
                      flex: 1,
                    }}
                    p="xs"
                  >
                    <Group wrap="nowrap" align="flex-start" gap="xs">
                      <LogMessage
                        message={log.message}
                        isExpanded={isExpanded}
                        onToggle={() => toggleLogExpansion(index)}
                      />

                      <CopyButton value={log.message}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? "Copied" : "Copy message"}>
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              onClick={copy}
                              color={copied ? "teal" : "gray"}
                              style={{ flexShrink: 0 }}
                            >
                              {copied ? (
                                <IconCheck size={14} />
                              ) : (
                                <IconCopy size={14} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Group>
                  </Paper>
                </Group>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
